import { vehicleService, productService } from '@/services'
import { handleRequestError } from '@/services/apiErrorHandler'
import { mapActions } from 'vuex'
// import logger from '@/logger/index'

// Marcas que não devem ser incluídas no search
const EXCLUDED_BRANDS = ['VOLKSWAGEN', 'CHEVROLET']

// Função utilitária para montar o search query respeitando as restrições de marca
const buildSearchQuery = (brand, searchValue) => {
  if (!searchValue) {
    return null
  }

  // Se não tiver marca, retorna apenas o searchValue
  if (!brand) {
    return searchValue.trim()
  }

  const brandUpper = brand.toUpperCase().trim()
  const shouldExcludeBrand = EXCLUDED_BRANDS.includes(brandUpper)

  // Se for uma das marcas excluídas, retorna apenas o searchValue
  return shouldExcludeBrand ? searchValue.trim() : `${brand} ${searchValue}`.trim()
}

const TYPES = {
  SET_REGISTRATION_PLATE: 'SET_REGISTRATION_PLATE',
  SET_VEHICLE_BRAND: 'SET_VEHICLE_BRAND',
  SET_VEHICLE_MODEL: 'SET_VEHICLE_MODEL',
  SET_SEARCH_SUGGESTIONS: 'SET_SEARCH_SUGGESTIONS',
  SELECT_SEARCH_SUGGESTION: 'SELECT_SEARCH_SUGGESTION',
  SET_ITEMS_TABLE: 'SET_ITEMS_TABLE',
  SET_TABLE_SCROLL_POSITION: 'SET_TABLE_SCROLL_POSITION',
  SET_PRODUCT_SEARCH_LOCK: 'SET_PRODUCT_SEARCH_LOCK',
}

const state = () => ({
  items: [],
  brand: null,
  vehicle: [],
  listSuggestions: [],
  registrationPlate: null,
  selectedSuggestion: null,
  tableScrollPosition: 0,
  // Trava de execução da busca por descrição do veículo. Fica aqui e não no
  // componente porque o Tab usa v-if: trocar de aba destrói o VehiclePlateSearch
  // e zeraria a trava, deixando o Enter repetido escapar.
  productSearchLock: { key: null, at: 0 },
})

const getters = {
  getVehicle: state => {
    return state.vehicle
  },

  getVehicleBrand: state => {
    return state.brand
  },

  getModel: state => {
    return state.vehicle?.busca
  },

  getRelevancia: state => {
    return state.vehicle?.relevancia
  },

  getListSuggestions: state => {
    return state.listSuggestions
  },

  getRegistrationPlate: state => {
    return state.registrationPlate
  },

  getSelectedSuggestion: state => {
    return state.selectedSuggestion
  },

  getItemsTable: state => {
    return state.items
  },

  getTableScrollPosition: state => {
    return state.tableScrollPosition
  },

  getProductSearchLock: state => {
    return state.productSearchLock
  },
}

const actions = {
  getVehiclePlate ({ commit, dispatch }, params) {
    commit(TYPES.SET_VEHICLE_MODEL, null)
    return new Promise((resolve, reject) => {
      vehicleService.apiPlate({ plate: params })
        .then(({ data }) => {
          commit(TYPES.SET_SEARCH_SUGGESTIONS, [])
          commit(TYPES.SELECT_SEARCH_SUGGESTION, null)
          commit(TYPES.SET_ITEMS_TABLE, [])
          if (data.respostav2 && data.respostav2[0]) {
            commit(TYPES.SET_VEHICLE_MODEL, data)
            commit(TYPES.SET_VEHICLE_BRAND, data)
            if (data?.respostav2.length === 1) {
              dispatch('handleSearchSuggestions', {
                brand: data?.completa[0]?.marca,
                search: data?.respostav2[0]?.busca,
              })
            }
          } else {
            dispatch('setFeedback',
              {
                title: 'Placa não encontrada',
                message: 'Desculpe, não encontramos na nossa base de dados.',
                type: 'error',
              },
              { root: true }
            )
          }
          resolve(data)
        })
        .catch(error => {
          dispatch('setFeedback',
            {
              title: 'A placa de veículo é inválida',
              message: 'Por favor verifique e tente novamente.',
              type: 'error',
            },
            { root: true }
          )
          handleRequestError(error, reject)
        })
    })
  },

  handleSearchSuggestions ({ commit }, params) {
    commit(TYPES.SET_SEARCH_SUGGESTIONS, [])
    const brand = params.brand
    // Trata quando search é string ou objeto
    const searchValue =
      typeof params.search === 'string' ? params.search : params.search?.busca

    // Usa a função utilitária para montar o search respeitando as restrições
    const search = buildSearchQuery(brand, searchValue)

    if (!search || !searchValue) {
      return Promise.resolve([])
    }
    return new Promise((resolve, reject) => {
      vehicleService.searchSuggestions({ model: search })
        .then((response) => {
          const data = response?.data || []
          commit(TYPES.SET_SEARCH_SUGGESTIONS, data)
          resolve(data)
        })
        .catch(error => handleRequestError(error, reject))
    })
  },

  addRegistrationPlate ({ commit }, params) {
    commit(TYPES.SET_REGISTRATION_PLATE, params)
  },

  selectSearchSuggestion ({ commit }, params) {
    commit(TYPES.SELECT_SEARCH_SUGGESTION, params)
  },

  searchProducts ({ commit, state }, params) {
    const brand = state.brand
    const searchValue = params.search

    const search = buildSearchQuery(brand, searchValue)

    if (!search) {
      return Promise.resolve([])
    }

    const updatedParams = {
      ...params,
      search: search,
    }

    commit(TYPES.SET_ITEMS_TABLE, [])
    return new Promise((resolve, reject) => {
      productService.search(updatedParams)
        .then((response) => {
          const data = response?.data || []
          commit(TYPES.SET_ITEMS_TABLE, data)
          resolve(data)
        })
        .catch(error => handleRequestError(error, reject))
    })
  },

  setTableScrollPosition ({ commit }, position) {
    commit(TYPES.SET_TABLE_SCROLL_POSITION, position)
  },

  setProductSearchLock ({ commit }, lock) {
    commit(TYPES.SET_PRODUCT_SEARCH_LOCK, lock)
  },
}

const mutations = {
  [TYPES.SET_VEHICLE_MODEL] (state, payload) {
    // if (payload) console.log('payload', payload)
    let vehicle = state.vehicle
    vehicle = payload?.respostav2 || []

    state.vehicle = vehicle
  },

  [TYPES.SET_VEHICLE_BRAND] (state, payload) {
    if (payload && payload.completa && payload.completa.marca) {
      state.brand = payload.completa.marca
    } else {
      state.brand = null
    }
  },

  [TYPES.SET_SEARCH_SUGGESTIONS] (state, payload) {
    state.listSuggestions = [...payload]
  },

  [TYPES.SELECT_SEARCH_SUGGESTION] (state, payload) {
    state.selectedSuggestion = payload
  },

  [TYPES.SET_REGISTRATION_PLATE] (state, payload) {
    state.registrationPlate = payload
  },

  [TYPES.SET_ITEMS_TABLE] (state, payload) {
    state.items = [...payload]
  },

  [TYPES.SET_TABLE_SCROLL_POSITION] (state, payload) {
    state.tableScrollPosition = payload
  },

  [TYPES.SET_PRODUCT_SEARCH_LOCK] (state, payload) {
    state.productSearchLock = {
      key: payload?.key ?? null,
      at: payload?.at ?? 0,
    }
  },
}

export default {
  methods: {
    ...mapActions(['setHeaderBack', 'setFeedback']),
  },
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
